<template lang="pug">
    .generative-faq
        .banner-message(v-if="shouldShowBanner")
          aiq-button(link @click="closeBanner")
            fa-icon(:icon="['fas', 'xmark']")
          .wrapper
            p {{ $t('gen_ai.banner.message') }}
        .intro
            img.logo(:src="returnCurrentLoaderStatusSrc")
        .message-box
          .message(v-for="item in messageList" :class="item?.isQuestion ? 'is-question' : 'is-response'")
            span(v-html="item?.message")
            .message-file-container(v-if="item?.imageUrl")
              ImageContainer(:imageUrl="item?.imageUrl")
            a.file(v-else-if="!!item?.file" :href="item?.file?.url" target="_blank")
              .icon-container(:style="{ backgroundColor: item?.file?.bgColor }")
                fa-icon(:icon="['far', item?.file?.icon]" v-if="item?.file?.icon==='globe-pointer'")
                fa-icon(:icon="['fal', item?.file?.icon]" v-else)
              .info
                span.title {{item?.file?.name}}
                span.format {{item?.file?.format}}
          .feedback-box(v-if="shouldShowFeedback")
            aiq-button(link @click="dismissFeedback")
              fa-icon(:icon="['fas', 'xmark']")
            p {{ $t('gen_ai.feedback.question') }}
            .response-container
              aiq-button(link @click="sendFeedback('like')")
                fa-icon(:icon="[getFeedbackIconType('like'), 'thumbs-up']" class="feedback-icon")
              aiq-button(link @click="sendFeedback('unlike')")
                fa-icon(:icon="[getFeedbackIconType('unlike'), 'thumbs-down']" class="feedback-icon")
        .input-box-wrapper
          .input-box
            aiq-input(size="small"
                placeholder="Ask a question"
                v-model="question"
                @keydown.enter="generateResponse")
</template>

<script>
import logo from '@/assets/generative-faq.png';
import logoLoading from '@/assets/generative-faq-load.gif';
import logoSuccess from '@/assets/generative-faq-success.png';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { marked } from 'marked';
import markedLinkifyIt from 'marked-linkify-it';
import LinkifyIt from 'linkify-it';
import GetGlobalSource from '@/libs/mixins/GetGlobalSource';
import { IMAGE_ACL_TYPES } from '@/constants';
import { mapState } from 'vuex';
import ImageContainer from './ImageContainer.vue';
import dayjs from '@/localPlugins/dayjs';

export default {
  name: 'GenerativeFaq',
  components: {
    ImageContainer,
  },
  data() {
    return {
      question: '',
      isResponseGenerating: false,
      pastConversation: '',
      sessionId: 0,
      previewBoxVisible: false,
      isBannerClosed: false,
      showFeedback: false,
      latestQueryId: 0,
      feedbackIconType: {'like': 'far', 'unlike': 'far'},
    };
  },
  computed: {
    ...mapState({
      messageList: state => state.genAi.messageList,
      agent: state => state.agent.profile,
    }),

    returnCurrentLoaderStatusSrc() {
      if (this.isResponseGenerating) return logoLoading;
      if (this.messageList?.length) return logoSuccess;
      return logo;
    },

    shouldShowBanner() {
      if (this.isBannerClosed) {
        return false;
      }
      const endOfDay = localStorage.getItem(`showSmartlynqBanner-${this.agent.id}`);
      if (endOfDay === null) {
        return true;
      }
      const now = dayjs();
      return now > dayjs(endOfDay);
    },
    shouldShowFeedback() {
      return this.showFeedback;
    },
  },
  methods: {
    async generateResponse() {
      this.isResponseGenerating = true;
      const question = this.question;
      this.addToMessages({question: question, answers: [], documents: []});
      if (this.showFeedback) {
        this.dismissFeedback();
      }
      this.question = '';
      try {
        const response = await this.$store.dispatch('genAi/generateFAQ', {query: question, pastConversations: this.pastConversation, sessionId: this.sessionId});
        const isAnswerAvailable = get(response, 'results.llm[0].is_answered', false) === true;
        const hasImage = isAnswerAvailable && get(response, 'results.image_path', '') !== '';
        let imageUrl = '';
        if (hasImage) {
          imageUrl = await this.getFileUrl({'key': response.results.image_path, 'acl': IMAGE_ACL_TYPES.PRIVATE});
        }
        this.addToMessages({
          question: null, answers: get(response, 'results.llm', []),
          documents: isAnswerAvailable ? get(response, 'results.sources', []) : [],
          imageUrl: hasImage ? imageUrl : null,
          backupFileName: question});
        this.pastConversation = [this.pastConversation, 'User:', question, 'Assistant:', get(response, 'results.llm[0].answer', '')].join(' ');
        this.sessionId = get(response, 'session_id');
        this.latestQueryId = get(response, 'id');
        this.showFeedback = true;
      } catch (error) {
        this.$aiq.notify.error('Unable to find an answer');
      } finally {
        this.isResponseGenerating = false;
      }
    },
    async addToMessages({question, answers, documents, imageUrl, backupFileName}) {
      const messages = [...this.messageList];
      if (question) {
        if (messages.length === 0 && this.shouldShowBanner) {
          this.closeBanner();
        }
        messages.push({
          message: question,
          isQuestion: true,
        });
      }
      for (const item of answers) {
        const processedAnswer = this.convertPrivateUrlToPublic(documents, get(item, 'answer', ''));
        const document = this.getDocument(documents, processedAnswer);
        messages.push({
          message: this.returnMarkedDownFormatted(processedAnswer),
          isQuestion: false,
          file: this.extractFileDetail(document, backupFileName),
          imageUrl: imageUrl,
        });
      }
      await this.$store.dispatch('genAi/updateMessages', messages);
      this.$nextTick(() => {
        this.scrollToEnd();
      });
    },
    extractFileDetail(url, backupFileName) {
      if (url) {
        try {
          const filename = decodeURIComponent(url.split('/').pop() || '');

          if (filename.includes('pdf') || filename.includes('docx')) {
            const parts = filename.split('.');
            const name = parts.slice(0, -1).join('.') || backupFileName;
            const isPdf = filename.includes('pdf');

            return {
              name,
              url,
              format: isPdf ? 'PDF' : 'DOCX',
              icon: isPdf ? 'file-pdf' : 'file-word',
              bgColor: isPdf ? '#B90909' : '#097624',
            };
          } else {
            const parts = url.split('/');
            const slug = parts.filter(Boolean).pop();
            const urlTitle = slug
              .replace(/-/g, ' ')
              .replace(/\b\w/g, char => char.toUpperCase());
            return {
              name: urlTitle || backupFileName,
              url,
              format: 'URL',
              icon: 'globe-pointer',
              bgColor: '#007BFF',
            };
          }
        } catch (err) {
          return null;
        }
      }
      return null;
    },
    scrollToEnd() {
      const container = this.$el.querySelector('.message-box');
      container.scrollTop = container.scrollHeight;
    },
    returnMarkedDownFormatted(text) {
      const schemas = {};
      const options = {truncate: 50}; // this truncates url with length > 50
      marked.use(markedLinkifyIt(schemas, options));
      const renderer = new marked.Renderer();
      const linkRenderer = renderer.link;
      renderer.link = (href, title, text) => {
        const html = linkRenderer.call(renderer, href, title, text);
        return html.replace(/^<a /, '<a target="_blank" rel="nofollow" ');
      };
      return marked(text, {renderer});
    },
    closeBanner() {
      const endOfDay = dayjs().endOf('day');
      localStorage.setItem(`showSmartlynqBanner-${this.agent.id}`, endOfDay);
      this.isBannerClosed = true;
    },
    dismissFeedback() {
      this.disableFeedback = {'like': false, 'unlike': false};
      this.feedbackIconType = {'like': 'far', 'unlike': 'far'};
      this.showFeedback = false;
    },
    async sendFeedback(option) {
      if (this.feedbackIconType[option] === 'fas') {
        this.feedbackIconType[option] = 'far';
      } else {
        this.feedbackIconType[option] = 'fas';
      }
      await this.$store.dispatch('genAi/sendFeedback', [this.latestQueryId, option]);
      this.dismissFeedback();
    },
    getFeedbackIconType(option) {
      return this.feedbackIconType[option];
    },
    getDocument(documents, answer) {
      if (isEmpty(documents)) {
        return null;
      }
      const schemas = {};
      const options = {};
      const linkify = LinkifyIt(schemas, options);
      const matches = linkify.match(answer);
      if (!matches || isEmpty(matches)) {
        return documents[0].url;
      }
      // match url in answer and sort by position from last to first. Linkify doc for the structure
      // of match can be found here https://www.npmjs.com/package/linkify-it#matchtext.
      matches.sort((a, b) => b.index - a.index);
      let picked;
      for (const match of matches) {
        // Pick the first match that is a http url.
        if (match.schema.indexOf('http') >= 0 || match.url.indexOf('http') >= 0) {
          picked = match;
          break;
        }
      }
      // If no matches, just return null.
      if (!picked) {
        return documents[0].url;
      }
      for (const doc of documents) {
        // Could have used picked.url but it is possible for its scheme to differ from the returned doc. (http vs https).
        // Using indexof for picked.raw is safer since it should be contained within the full doc url.
        if (doc.url.indexOf(picked.raw) >= 0) {
          return doc.url;
        }
      }
      // pick the first document from sources if none of the docs matches.
      return documents[0].url;
    },
    convertPrivateUrlToPublic(documents, answer) {
      // Replace any matched URL fragments in 'answer' with a full public URL
      // from the 'documents' list. This is necessary because the answer might contain
      // private urls that must be resolved to public links.

      if (isEmpty(documents)) {
        // If there are no public document URLs to map to, skip the expensive
        // link detection and string manipulation process and return the original answer.
        return answer;
      }
      const schemas = {};
      const options = {};
      const linkify = LinkifyIt(schemas, options);
      const matches = linkify.match(answer);
      if (!matches || isEmpty(matches)) {
        return answer;
      }
      const answerArray = [];

      // Critical Implementation Detail: We must process matches in order of appearance (ascending index)
      // to correctly rebuild the final string by segmenting the text around each match.
      matches.sort((a, b) => a.index - b.index);

      let lastIndex = 0; // The index in 'answer' immediately following the last processed segment.

      for (const match of matches) {
        // Pick the first match that is a http url.
        if (match.schema.indexOf('http') >= 0 || match.url.indexOf('http') >= 0) {

          // Append the text segment that occurred *before* the current link match.
          answerArray.push(answer.substring(lastIndex, match.index));
          let found = false;
          for (const doc of documents) {
            if (doc.url.indexOf(match.raw) >= 0) {
              found = true;
              answerArray.push(doc.url);
              break;
            }
          }

          // If no document match was found, we simply retain the linkify-resolved URL.
          if (!found) {
            answerArray.push(match.url);
          }
          lastIndex = match.lastIndex;
        }
      }
      // Append the final remaining text segment after the last processed link.
      answerArray.push(answer.substring(lastIndex));

      // Reconstruct the final string from all collected text and substituted URLs.
      return answerArray.join('');
    },
  },
  mixins: [GetGlobalSource],
};
</script>

<style lang="scss" scoped>
.generative-faq {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  box-sizing: content-box;
  .banner-message {
    position: relative;
    margin-bottom: 10px;
    background-color: #4671B3;
    padding: 5px;
    .wrapper {
      margin-right: 20px;
      margin-left: 10px;
      font-size: 12px;
      text-align: center;
      p {
        color: #fff !important;
      }
    }
    button {
      display: block;
      position: absolute;
      top: 5px;
      right: 5px;
      font-size: 1.4em;
      color: white;
      &:hover {
        color: #eee;
      }
    }
  }
  .intro {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 15px;
    .logo {
      width: 76px;
      height: 100px;
    }
    span {
      color: #28292c;
      font-size: 12px;
      text-align: center;
    }
  }
  .input-box-wrapper {
    width: 100%;
    position: relative;
    margin-top: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    ::v-deep input {
      padding-right: 25px;
    }
    .input-box {
      width: 100%;
      svg {
        position: absolute;
        right: 5px;
      }
    }
  }
  .message-box {
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    width: 100%;
    margin: 25px 0;
    color: #28292c;
    overflow-y: auto;
    scroll-behavior: smooth;
    .message{
      display: flex;
      flex-direction: column;
      row-gap: 10px;
      ::v-deep p {
        a {
          width: 100%;
          text-overflow: ellipsis;
          display: inline-block;
          overflow: hidden;
        }
      }
      ::v-deep ul{
        list-style: decimal;
        padding-left: 15px;
        margin: 5px 0;
        li{
          list-style: decimal;
        }
      }
    }
    .file {
      height: 45px;
      display: flex;
      background: #414246;
      border-radius: 4px;
      padding: 5px 10px;
      color: #fff;
      column-gap: 10px;
      align-items: center;
      overflow: hidden;
      .icon-container{
        width: 30px;
        height: 30px;
        min-width: 30px;
        min-height: 30px;
        border: 1px solid #fff;
        border-radius: 4px;
        overflow: hidden;
        font-size: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .info {
        display: flex;
        flex-direction: column;
        font-size: 14px;
        width: 100%;
        .title{
          width: 50%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: inline-block;
        }
        .format{
          font-size: 12px;
          font-weight: 300;
        }
      }
    }
    .is-question {
      background: #e4ebf4;
      padding: 5px 10px;
      border-radius: 4px;
      margin-left: auto;
    }
    .is-response{
      padding-right: 10px;
    }

    .feedback-box {
      width: 100%;
      margin-top: auto;
      margin-bottom: 10px;
      position: relative;
      background: #eaeaeb;
      border-radius: 2px;
      padding: 10px;
      & > button {
        position: absolute;
        top: 10px;
        right: 10px;
      }
      p {
        font-size: 14px;
        margin-bottom: 10px;
      }
      .response-container {
        display: flex;
        justify-content: center;
        .feedback-icon {
          width: 30px;
          height: 30px;
          -webkit-transition: color .5s;
          -moz-transition: color .5s;
          -o-transition: color .5s;
          transition: color .5s;
          &.fa-thumbs-up {
            color: #008000;
          }
          &.fa-thumbs-down {
            color: #cc0000;
          }
          &:hover {
            &.fa-thumbs-up {
              color: #00a000;
            }
            &.fa-thumbs-down {
              color: #ff0000;
            }
          }
        }
      }
    }
  }
  .message-box, .input-box-wrapper {
    padding-right: 32px;
    padding-left: 32px;
    box-sizing: border-box;
  }
}
</style>
